import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  ClockIcon,
  HashtagIcon,
  RocketLaunchIcon,
  ChevronRightIcon,
  FlagIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { TemperaturePill, DispositionBadge, GoalOutcomeIcon, FollowUpStatusBadge } from '../../components/intelligence';
import PageLoader from '../../components/PageLoader';
import SendSMSModal from '../../components/SendSMSModal';
import SendEmailModal from '../../components/SendEmailModal';
import Button from '../../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';
import { createContactFromReferral } from '../../services/api';
import { useLeadDetailPage } from './useLeadDetailPage';
import type { CallSummary } from '../RunProgressPage/runProgressTypes';

function aggregateIntelligence(callHistory: CallSummary[]) {
  if (callHistory.length === 0) return null;

  const latest = callHistory[0];

  const dmConfirmed = callHistory.find((c) => c.decision_maker_confirmed === true);
  const dmNotes = callHistory.map((c) => c.decision_maker_notes).filter(Boolean);
  const hasDmInfo = callHistory.some((c) => c.decision_maker_confirmed !== undefined && c.decision_maker_confirmed !== null);

  const allObjections = new Set<string>();
  for (const c of callHistory) {
    if (c.objections) c.objections.forEach((o) => allObjections.add(o));
  }

  const followUps = callHistory.filter(
    (c) => c.follow_up_action?.action_required
  );
  const pendingFollowUps = followUps.filter((c) => !c.follow_up_status);

  const collectedInfo: Record<string, string> = {};
  // LT-502 — track the source CallSummary that supplied the referral so the
  // "Convert to contact" button can pass it to the backend. We pick the same
  // call the displayed referral_name/_phone came from (first non-empty in
  // descending-timestamp order), matching the prioritisation below.
  let referralCallSummaryId: string | null = null;
  for (const c of callHistory) {
    const info = c.lead_provided_info;
    if (!info) continue;
    if (info.email && !collectedInfo.email) collectedInfo.email = info.email;
    if (info.referral_name && !collectedInfo.referral_name) {
      collectedInfo.referral_name = info.referral_name;
      if (info.referral_phone) referralCallSummaryId = c.id;
    }
    if (info.referral_phone && !collectedInfo.referral_phone) {
      collectedInfo.referral_phone = info.referral_phone;
      if (!referralCallSummaryId && info.referral_name) referralCallSummaryId = c.id;
    }
    if (info.preferred_callback_time && !collectedInfo.preferred_callback_time) collectedInfo.preferred_callback_time = info.preferred_callback_time;
    if (info.other && !collectedInfo.other) collectedInfo.other = info.other;
  }

  const goalAchieved = callHistory.filter((c) => c.goal_outcome === 'achieved').length;
  const goalPartial = callHistory.filter((c) => c.goal_outcome === 'partial').length;
  const goalNone = callHistory.filter((c) => c.goal_outcome === 'no_progress').length;

  return {
    latest,
    dmConfirmed: !!dmConfirmed,
    hasDmInfo,
    dmNotes: dmNotes[0] ?? null,
    objections: Array.from(allObjections),
    followUps,
    pendingFollowUps,
    collectedInfo,
    hasCollectedInfo: Object.keys(collectedInfo).length > 0,
    referralCallSummaryId,
    goalAchieved,
    goalPartial,
    goalNone,
    temperatureReason: latest.temperature_reason ?? null,
  };
}

const LeadDetailPage: React.FC = () => {
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { lead, callHistory, campaigns, loading, error } = useLeadDetailPage(leadId);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // LT-502 — referral-to-contact conversion state.
  const [convertingReferral, setConvertingReferral] = useState(false);

  const handleConvertReferral = async (callSummaryId: string) => {
    setConvertingReferral(true);
    try {
      const result = await createContactFromReferral(callSummaryId);
      if (!result.created) {
        setToast({ type: 'success', message: 'Contact already exists — opening' });
      }
      navigate(`/leads/${result.contact_id}`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const message =
        (detail && typeof detail === 'object' && detail.message) ||
        (typeof detail === 'string' ? detail : null) ||
        'Failed to create contact from referral';
      setToast({ type: 'error', message });
    } finally {
      setConvertingReferral(false);
    }
  };

  if (loading) return <PageLoader message="Loading lead details..." />;
  if (error) return (
    <div className="p-6">
      <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">{error}</div>
    </div>
  );
  if (!lead) return (
    <div className="p-6">
      <div className="bg-yellow-500/20 border border-yellow-400 text-yellow-100 px-4 py-3 rounded-lg">Lead not found</div>
    </div>
  );

  const intel = aggregateIntelligence(callHistory);

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6 max-w-[1200px] mx-auto">
        {/* Header with badges */}
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-3 sm:p-4 mb-4">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{lead.name}</h1>
            <TemperaturePill temperature={lead.current_temperature} />
            <DispositionBadge disposition={lead.current_disposition} />
            {intel?.hasDmInfo && (
              intel.dmConfirmed ? (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                  <CheckCircleIcon className="h-3.5 w-3.5" /> Decision Maker
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border bg-amber-500/15 text-amber-400 border-amber-500/30">
                  <QuestionMarkCircleIcon className="h-3.5 w-3.5" /> DM Unconfirmed
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400 flex-wrap">
            <span className="flex items-center gap-1">
              <PhoneIcon className="h-3.5 w-3.5" /> {lead.phone}
            </span>
            {lead.email && (
              <span className="flex items-center gap-1">
                <EnvelopeIcon className="h-3.5 w-3.5" /> {lead.email}
              </span>
            )}
            {canSms && (
              <button
                type="button"
                onClick={() => setShowSmsModal(true)}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" /> Send SMS
              </button>
            )}
            {(lead.email || '').trim() && (
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-xs font-medium text-sky-300 hover:bg-sky-500/20 transition-colors"
                title="Send email"
              >
                <EnvelopeIcon className="h-3.5 w-3.5" /> Send email
              </button>
            )}
          </div>
          {/* Stats row */}
          <div className="flex flex-wrap gap-4 sm:gap-6 mt-3 pt-3 border-t border-slate-700/30 text-sm">
            <StatItem icon={HashtagIcon} label="Attempts" value={String(lead.total_attempts ?? lead.retry_count)} />
            <StatItem icon={RocketLaunchIcon} label="Campaigns" value={String(campaigns.length)} />
            <StatItem icon={CalendarIcon} label="First seen" value={new Date(lead.created_at).toLocaleDateString()} />
            {lead.last_contact_at && (
              <StatItem icon={ClockIcon} label="Last contact" value={new Date(lead.last_contact_at).toLocaleDateString()} />
            )}
          </div>
        </div>

        {/* Intelligence panels - two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Follow-ups */}
          {intel && intel.followUps.length > 0 && (
            <div className="rounded-lg border-l-4 border-amber-500 bg-amber-500/10 p-3">
              <div className="flex items-center gap-2 mb-2">
                <FlagIcon className="h-4 w-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-amber-300">
                  Follow-ups ({intel.followUps.length})
                </h3>
                <span className="text-[10px] text-amber-200/80 bg-amber-500/20 border border-amber-500/30 rounded px-1.5 py-0.5">
                  {intel.pendingFollowUps.length} pending
                </span>
              </div>
              <div className="space-y-2">
                {intel.followUps.map((cs, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left rounded-md bg-slate-800/30 p-2 hover:bg-slate-800/50 transition-colors"
                    onClick={() => {
                      if (lead.run_id) navigate(`/runs/${lead.run_id}/calls/${cs.id}`);
                    }}
                  >
                    {cs.follow_up_action!.urgency && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium mr-1 ${
                        cs.follow_up_action!.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                        cs.follow_up_action!.urgency === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {cs.follow_up_action!.urgency}
                      </span>
                    )}
                    {cs.follow_up_action!.action_type && (
                      <span className="text-xs text-slate-400">{cs.follow_up_action!.action_type.replace(/_/g, ' ')}</span>
                    )}
                    {cs.follow_up_action!.description && (
                      <p className="text-xs text-slate-300 mt-1 line-clamp-2">{cs.follow_up_action!.description}</p>
                    )}
                    <div className="mt-1.5">
                      {cs.follow_up_status ? (
                        <FollowUpStatusBadge status={cs.follow_up_status} />
                      ) : (
                        <span className="inline-flex items-center rounded-md border border-amber-500/30 px-2 py-0.5 text-[10px] font-medium text-amber-300 bg-amber-500/20">
                          Pending
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Decision Maker */}
          {intel?.hasDmInfo && (
            <Section title="Decision Maker" icon={UserIcon}>
              <div className="flex items-center gap-2 mb-1">
                {intel.dmConfirmed ? (
                  <span className="text-xs text-emerald-400 font-medium">Confirmed</span>
                ) : (
                  <span className="text-xs text-amber-400 font-medium">Not confirmed</span>
                )}
              </div>
              {intel.dmNotes && (
                <p className="text-sm text-slate-300">{intel.dmNotes}</p>
              )}
            </Section>
          )}

          {/* Objections */}
          {intel && intel.objections.length > 0 && (
            <Section title="Objections Raised" icon={ChatBubbleLeftRightIcon}>
              <div className="space-y-1.5">
                {intel.objections.map((obj, i) => {
                  const text = typeof obj === 'string' ? obj : (obj as { objection?: string }).objection || JSON.stringify(obj);
                  const response = typeof obj === 'object' && obj !== null ? (obj as { response?: string }).response : undefined;
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                      <div>
                        <span className="text-sm text-slate-300">{text}</span>
                        {response && <p className="text-xs text-slate-400 mt-0.5">Response: {response}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Collected Info */}
          {intel?.hasCollectedInfo && (
            <Section title="Collected Info" icon={EnvelopeIcon}>
              <div className="space-y-2">
                {intel.collectedInfo.email && <InfoRow label="Email" value={intel.collectedInfo.email} />}
                {intel.collectedInfo.referral_name && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <InfoRow label="Referral" value={intel.collectedInfo.referral_name} />
                    {/* LT-502 — only enable when both name + phone are captured
                        AND we know which call_summary supplied them. */}
                    {intel.collectedInfo.referral_phone && intel.referralCallSummaryId && (
                      <Button
                        intent="secondary"
                        size="sm"
                        disabled={convertingReferral}
                        onClick={() => handleConvertReferral(intel.referralCallSummaryId!)}
                        data-testid="convert-referral-button"
                      >
                        {convertingReferral ? 'Converting...' : 'Convert to contact'}
                      </Button>
                    )}
                  </div>
                )}
                {intel.collectedInfo.referral_phone && <InfoRow label="Ref. Phone" value={intel.collectedInfo.referral_phone} />}
                {intel.collectedInfo.preferred_callback_time && <InfoRow label="Callback" value={intel.collectedInfo.preferred_callback_time} />}
                {intel.collectedInfo.other && <InfoRow label="Other" value={intel.collectedInfo.other} />}
              </div>
            </Section>
          )}

          {/* Temperature Reasoning */}
          {intel?.temperatureReason && (
            <Section title="Why this rating?" icon={ChatBubbleLeftRightIcon}>
              <p className="text-sm text-slate-300">{intel.temperatureReason}</p>
            </Section>
          )}

          {/* Campaigns Involved */}
          {campaigns.length > 0 && (
            <div className="rounded-lg border border-slate-700/50 bg-slate-800/30 overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-700/30 flex items-center gap-2">
                <RocketLaunchIcon className="h-4 w-4 text-cyan-400" />
                <h3 className="text-sm font-semibold text-white">Campaigns</h3>
              </div>
              <div className="divide-y divide-slate-700/30">
                {campaigns.map((c) => (
                  <Link
                    key={c.run_id}
                    to={`/runs/${c.run_id}`}
                    className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-800/50 transition-colors"
                  >
                    <span className="text-sm text-white truncate">{c.name}</span>
                    <ChevronRightIcon className="h-3 w-3 text-slate-400 ml-auto flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Call History - full width */}
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50">
          <div className="p-3 sm:p-4 border-b border-slate-700/30">
            <h2 className="text-sm sm:text-base font-semibold text-white">Call History</h2>
            <p className="text-xs text-slate-400 mt-0.5">{callHistory.length} call{callHistory.length !== 1 ? 's' : ''} total</p>
          </div>

          {callHistory.length === 0 ? (
            <div className="p-6 text-center">
              <ClockIcon className="h-8 w-8 text-slate-500 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No call history found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30">
              {callHistory.map((entry) => {
                const date = entry.timestamp ? new Date(entry.timestamp) : null;
                const dateStr = date?.toLocaleDateString() ?? '';
                const timeStr = date?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase() ?? '';
                const durationSec = entry.call_metadata?.duration_seconds;
                const hasDM = entry.decision_maker_confirmed !== undefined && entry.decision_maker_confirmed !== null;

                return (
                  <button
                    key={entry.id}
                    type="button"
                    className="w-full text-left px-3 sm:px-4 py-3 hover:bg-slate-800/30 transition-colors"
                    onClick={() => {
                      if (lead.run_id) navigate(`/runs/${lead.run_id}/calls/${entry.id}`);
                    }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Left: date + badges */}
                      <div className="flex-shrink-0 w-32">
                        <span className="text-xs text-slate-400 block">{dateStr} {timeStr}</span>
                        {durationSec != null && (
                          <span className="text-xs text-slate-500 block mt-0.5">{Math.floor(durationSec / 60)}m {Math.floor(durationSec % 60)}s</span>
                        )}
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <TemperaturePill temperature={entry.lead_temperature} />
                          <DispositionBadge disposition={entry.disposition} />
                        </div>
                      </div>
                      {/* Right: summary + meta */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <GoalOutcomeIcon outcome={entry.goal_outcome} />
                          {hasDM && (
                            entry.decision_maker_confirmed ? (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-400">
                                <CheckCircleIcon className="h-3 w-3" /> DM
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-400">
                                <QuestionMarkCircleIcon className="h-3 w-3" /> DM?
                              </span>
                            )
                          )}
                        </div>
                        {entry.analysis_summary && (
                          <p className="text-sm text-slate-300 line-clamp-2">{entry.analysis_summary}</p>
                        )}
                        {entry.follow_up_action?.action_required && (
                          <div className="text-xs text-amber-400 mt-1 flex items-center gap-2 flex-wrap">
                            <span>Follow-up: {entry.follow_up_action.description}</span>
                            {entry.follow_up_status ? (
                              <FollowUpStatusBadge status={entry.follow_up_status} />
                            ) : (
                              <span className="inline-flex items-center rounded-md border border-amber-500/30 px-1.5 py-0.5 text-[10px] font-medium text-amber-300 bg-amber-500/20">
                                Pending
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-full mx-4">
          <div className={`p-3 rounded-lg flex items-center justify-between shadow-lg ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <span className={`text-sm whitespace-pre-line ${toast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{toast.message}</span>
            <button type="button" onClick={() => setToast(null)} className="text-gray-400 hover:text-white ml-2">×</button>
          </div>
        </div>
      )}

      <SendSMSModal
        isOpen={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        lead={lead ? { id: lead.id, name: lead.name, phone: lead.phone } : null}
        setToast={setToast}
      />
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        lead={
          lead && (lead.email || '').trim()
            ? { id: lead.id, name: lead.name, email: (lead.email || '').trim() }
            : null
        }
        setToast={setToast}
      />
    </div>
  );
};

function Section({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-700/50 bg-slate-800/30 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function StatItem({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-400" />
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm text-white font-medium">{value}</p>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 w-20 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-300 break-all">{value}</span>
    </div>
  );
}

export default LeadDetailPage;
